import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from 'helpers/dateTimeFormatter';
import {
    CHART_THRESHOLD_EVENT_TOLERABLE_INDICATOR_TOOLTIP,
    CHART_THRESHOLD_EVENT_POOR_INDICATOR_TOOLTIP,
    CHART_CONDITION_TREND_STATUS_POOR,
    CHART_CONDITION_TREND_STATUS_TOLERABLE,
    TREND_LINE_COLOR,
    ALERT_LINE_COLOR
} from './constants';
import './styles.scss';

const DcLinkCapacitorTooltip = ({
    timeStamp,
    meanValue,
    unit,
    min,
    max,
    alertThreshold,
    chargingCycleNumber,
    conditionEvent
}) => {
    const { t: translate } = useTranslation();
    const formattedDate = timeStamp ? formatDateTime(timeStamp) : '-';
    const isPoor = conditionEvent?.newCondition === CHART_CONDITION_TREND_STATUS_POOR;
    const isTolerable = conditionEvent?.newCondition === CHART_CONDITION_TREND_STATUS_TOLERABLE;
    const hasEvent = isPoor || isTolerable;
    const eventIcon = isPoor
        ? CHART_THRESHOLD_EVENT_POOR_INDICATOR_TOOLTIP
        : CHART_THRESHOLD_EVENT_TOLERABLE_INDICATOR_TOOLTIP;

    return (
        <div className='custom-dc-link-capacitor-tooltip'>
            <div className='t-header'>
                <span className='t-date'>{formattedDate}</span>
            </div>
            {hasEvent &&
                <div className='t-event-section'>
                    <div className='t-event-row'>
                        <span className='t-event-icon'>
                            <img src={`data:image/svg+xml;utf8,${encodeURIComponent(eventIcon)}`} alt='' width='16' height='16' />
                        </span>
                        <span className='t-event-label'>
                            {translate('ABB.Powertrain.Frontend.dcLinkCapacitorThresholdCrossingEvent')}
                        </span>
                    </div>
                    {isPoor &&
                        <div className='t-event-detail'>
                            {translate('ABB.Powertrain.Frontend.dcLinkCapacitorSuccessiveIncreasingPeaks')}
                        </div>
                    }
                </div>
            }
            <div className='t-item'>
                <span className='symbol symbol--dot' style={{ backgroundColor: TREND_LINE_COLOR }} />
                <span className='t-label'>
                    {hasEvent
                        ? `${translate('ABB.Powertrain.Frontend.dcLinkCapacitorMeanLabel')}:`
                        : `${translate('ABB.Powertrain.Frontend.dcLinkCapacitorValueLabel')}:`}
                </span>
                <span className='t-value'>{meanValue ?? '-'} {unit}</span>
            </div>
            {hasEvent ?
                <>
                    <div className='t-item'>
                        <span className='symbol' style={{ backgroundColor: TREND_LINE_COLOR, opacity: '0.3' }} />
                        <span className='t-label'>{translate('ABB.Powertrain.Frontend.rangeLabel')}:</span>
                        <span className='t-value'>{min ?? '-'} / {max ?? '-'}</span>
                    </div>
                    {alertThreshold !== null &&
                        <div className='t-item'>
                            <span className='symbol symbol--dot' style={{ backgroundColor: ALERT_LINE_COLOR }} />
                            <span className='t-label'>{translate('ABB.Powertrain.Frontend.dcLinkCapacitorAlertThreshold')}:</span>
                            <span className='t-value'>{alertThreshold} {unit}</span>
                        </div>
                    }
                </>
                :
                <>
                    {alertThreshold !== null &&
                        <div className='t-item'>
                            <span className='symbol symbol--dot' style={{ backgroundColor: ALERT_LINE_COLOR }} />
                            <span className='t-label'>{translate('ABB.Powertrain.Frontend.dcLinkCapacitorAlertThreshold')}:</span>
                            <span className='t-value'>{alertThreshold} {unit}</span>
                        </div>
                    }
                    <div className='t-item'>
                        <span className='symbol' style={{ backgroundColor: TREND_LINE_COLOR, opacity: '0.3' }} />
                        <span className='t-label'>{translate('ABB.Powertrain.Frontend.rangeLabel')}:</span>
                        <span className='t-value'>{min ?? '-'} / {max ?? '-'}</span>
                    </div>
                </>
            }
            <div className='t-footer'>
                {translate('ABB.Powertrain.Frontend.dcLinkCapacitorChargingWithinHour')}: {chargingCycleNumber ?? 0}
            </div>
        </div>
    );
};

DcLinkCapacitorTooltip.propTypes = {
    timeStamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
    meanValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    unit: PropTypes.string,
    min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    alertThreshold: PropTypes.number,
    chargingCycleNumber: PropTypes.number,
    conditionEvent: PropTypes.shape({
        newCondition: PropTypes.string,
        oldCondition: PropTypes.string,
        timestamp: PropTypes.string,
    })
};

export default DcLinkCapacitorTooltip;
